/**
 * Settings › Security — two-step verification for the signed-in account,
 * plus (for a company owner) the company-wide requirement.
 *
 * Server: `apps/api/src/trpc/routers/wagepro_two_factor.ts`. Only a
 * first-party WagePro session can manage this here; someone who reached
 * WagePro through "Sign in with Ledger" manages two-step verification in
 * their Ledger account, so this section says that instead of showing
 * controls that would be refused.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Badge,
  Button,
  GlassCard,
  InfoBanner,
  Skeleton,
  Switch,
} from '@jumpstone/ui-react';
import { getTrpcClient } from '../../lib/trpc.js';
import { useAuth } from '../../lib/auth/auth_context.js';
import {
  classifyTwoFactorError,
  type TwoFactorErrorKind,
} from '../../lib/auth/two_factor_logic.js';
import { TwoFactorError } from '../../components/auth/two_factor_error.js';
import { SecurityEnableFlow } from './security_enable_flow.js';
import { SecurityDisableForm } from './security_disable_form.js';

interface TwoFactorStatus {
  enabled: boolean;
  recovery_codes_remaining: number;
  required: boolean;
  company: { require_two_factor: boolean } | null;
}

type Panel = 'none' | 'enable' | 'disable';

export function SettingsSecuritySection(): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { bootSource } = useAuth();
  const managedInLedger = bootSource === 'ledger_grant';

  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loadError, setLoadError] = useState<TwoFactorErrorKind | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [panel, setPanel] = useState<Panel>('none');
  const [savingRequirement, setSavingRequirement] = useState(false);
  const [requirementError, setRequirementError] =
    useState<TwoFactorErrorKind | null>(null);

  useEffect(() => {
    if (managedInLedger) return;
    let cancelled = false;
    getTrpcClient()
      .wageproTwoFactor.status.query()
      .then((next) => {
        if (!cancelled) {
          setStatus(next);
          setLoadError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(classifyTwoFactorError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [managedInLedger, reloadKey]);

  function reload(): void {
    setPanel('none');
    setReloadKey((key) => key + 1);
  }

  async function setRequirement(required: boolean): Promise<void> {
    setSavingRequirement(true);
    setRequirementError(null);
    try {
      await getTrpcClient().wageproTwoFactor.setRequirement.mutate({
        required,
      });
      reload();
    } catch (err) {
      setRequirementError(classifyTwoFactorError(err));
    } finally {
      setSavingRequirement(false);
    }
  }

  if (managedInLedger) {
    return (
      <GlassCard className="rounded-2xl p-5">
        <h2 className="text-base font-semibold text-[color:var(--ink)]">
          {t('two_factor.section_title')}
        </h2>
        <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
          {t('two_factor.managed_in_ledger')}
        </p>
      </GlassCard>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <GlassCard className="rounded-2xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-xl">
            <h2 className="text-base font-semibold text-[color:var(--ink)]">
              {t('two_factor.section_title')}
            </h2>
            <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
              {t('two_factor.section_dek')}
            </p>
          </div>
          {status !== null && (
            <Badge variant={status.enabled ? 'success' : 'neutral'}>
              {status.enabled
                ? t('two_factor.status_on')
                : t('two_factor.status_off')}
            </Badge>
          )}
        </div>

        <div className="mt-4">
          {loadError !== null ? (
            <TwoFactorError kind={loadError} />
          ) : status === null ? (
            <Skeleton className="nv-skel h-16 w-full" />
          ) : panel === 'enable' ? (
            <SecurityEnableFlow
              onCancel={() => setPanel('none')}
              onEnabled={reload}
            />
          ) : panel === 'disable' ? (
            <SecurityDisableForm
              onCancel={() => setPanel('none')}
              onDisabled={reload}
            />
          ) : status.enabled ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-[color:var(--ink-muted)]">
                {t('two_factor.recovery_remaining', {
                  count: status.recovery_codes_remaining,
                })}
              </p>
              {status.required ? (
                <p className="nv-help">
                  {t('two_factor.required_cannot_disable')}
                </p>
              ) : (
                <div>
                  <Button
                    variant="secondary"
                    onClick={() => setPanel('disable')}
                  >
                    {t('two_factor.turn_off')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {status.required && (
                <InfoBanner title={t('two_factor.required_banner_title')}>
                  {t('two_factor.required_banner_body')}
                </InfoBanner>
              )}
              <div>
                <Button variant="primary" onClick={() => setPanel('enable')}>
                  {t('two_factor.turn_on')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </GlassCard>

      {status !== null && status.company !== null && (
        <GlassCard className="rounded-2xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-xl">
              <h2 className="text-base font-semibold text-[color:var(--ink)]">
                {t('two_factor.company_title')}
              </h2>
              <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
                {t('two_factor.company_dek')}
              </p>
              {!status.enabled && !status.company.require_two_factor && (
                <p className="nv-help">
                  {t('two_factor.company_enable_self_first')}
                </p>
              )}
            </div>
            <Switch
              checked={status.company.require_two_factor}
              onCheckedChange={(next) => void setRequirement(next)}
              disabled={
                savingRequirement ||
                (!status.enabled && !status.company.require_two_factor)
              }
              aria-label={t('two_factor.company_title')}
            />
          </div>
          {requirementError !== null && (
            <div className="mt-3">
              <TwoFactorError kind={requirementError} />
            </div>
          )}
        </GlassCard>
      )}
    </div>
  );
}
